import React, { useMemo, useState, useCallback } from 'react';
import { GameState, Player, DisplayFixture, Cup, League } from '../../../domain/entities';
import { MatchDetailsModal } from '../../shared/MatchDetailsModal';
import { TeamBadge } from '../../shared/TeamBadge';
import { soundService } from '../../../services/SoundService';
import { FixturesHelper } from '../../../services/FixturesHelper';
import { View } from '../../../App';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy } from '@fortawesome/free-solid-svg-icons';
import { getLeagueColor } from '../../../utils/iconUtils';

interface AllFixturesViewProps {
    gameState: GameState;
    pushView: (view: View) => void;
    isAiPunditEnabled: boolean;
    showRatingsOnMatchReport: boolean;
    // FIX: Added missing onCacheAiMatchReport prop
    onCacheAiMatchReport: (fixture: DisplayFixture, report: { title: string; message: string; }) => void;
}

const getCompetitionEmoji = (competition: string, gameState: GameState) => {
    const cupNames = gameState.config.CUP_NAMES;
    if (competition === cupNames[0]) {
        return <FontAwesomeIcon icon={faTrophy} style={{ color: '#FFD700', marginRight: '4px' }} />; // Pixel Cup - gold
    }
    if (competition === cupNames[1]) {
        return <FontAwesomeIcon icon={faTrophy} style={{ color: '#C0C0C0', marginRight: '4px' }} />; // Junior Cup - silver
    }
    // League - use trophy with tier-based color
    return <FontAwesomeIcon icon={faTrophy} style={{ color: getLeagueColor(competition, gameState.config.LEAGUE_NAMES), marginRight: '4px' }} />;
};

export const AllFixturesView: React.FC<AllFixturesViewProps> = ({ gameState, pushView, isAiPunditEnabled, showRatingsOnMatchReport, onCacheAiMatchReport }) => {
    const [week, setWeek] = useState(Math.floor(gameState.currentWeek) || 1);
    const [selectedLeague, setSelectedLeague] = useState<string>('ALL');
    const [selectedFixture, setSelectedFixture] = useState<DisplayFixture | null>(null);
    const leagueNames = gameState.config.LEAGUE_NAMES;

    const getPlayerById = useCallback((id: number): Player | undefined => {
        // FIX: Use for...of and Object.values to iterate leagues to ensure correct type inference.
        // FIX: Cast return value of Object.values to ensure correct type.
        for (const league of Object.values(gameState.leagues) as League[]) {
            for (const team of league.teams) {
                const player = team.squad.find(p => p.id === id);
                if (player) return player;
            }
        }
        return undefined;
    }, [gameState.leagues]);

    const getTeamByName = useCallback((name: string) => {
        for (const league of Object.values(gameState.leagues)) {
            const team = league.teams.find(t => t.name === name);
            if (team) return team;
        }
        return undefined;
    }, [gameState.leagues]);

    const weeksPerSeason = gameState.config.WEEKS_PER_SEASON;

    const weekFixtures = useMemo(() => {
        const leaguesToSearch = selectedLeague === 'ALL' ? leagueNames : [selectedLeague];

        const leagueFixtures: DisplayFixture[] = leaguesToSearch.flatMap(leagueName =>
            FixturesHelper.getLeagueFixturesForWeek(gameState.fixtures, leagueName, week)
                .map(f => ({ ...f, competition: leagueName, type: 'league' }))
        );

        const cupFixtures: DisplayFixture[] = Object.values(gameState.cups)
            .flatMap((cup: Cup) =>
                FixturesHelper.getCupFixturesForWeek(gameState.fixtures, cup.name, week)
                    .map(f => ({ ...f, competition: cup.name, type: 'cup' }))
            );

        return [...leagueFixtures, ...cupFixtures].sort((a, b) => {
            if (a.week !== b.week) return a.week - b.week;
            if (a.type !== b.type) return a.type === 'league' ? -1 : 1;
            // Sort by league tier (order in leagueNames array) for leagues, alphabetically for cups
            if (a.competition !== b.competition) {
                const aIndex = leagueNames.indexOf(a.competition);
                const bIndex = leagueNames.indexOf(b.competition);
                if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
                return a.competition.localeCompare(b.competition);
            }
            return a.home.localeCompare(b.home);
        });
    }, [week, selectedLeague, gameState, leagueNames]);

    // Check if this week is a cup week (used for empty week message)
    const cupWeekInfo = useMemo(() => {
        for (const cup of Object.values(gameState.cups)) {
            const round = cup.rounds?.find(r => Math.floor(r.week) === week);
            if (round) {
                return { cupName: cup.name, roundName: round.name };
            }
        }
        return null;
    }, [week, gameState.cups]);

    const handleWeekChange = (newWeek: number) => {
        soundService.playClick();
        setWeek(newWeek);
    }

    const handleFixtureClick = (fixture: DisplayFixture) => {
        if (!fixture.result) return;
        soundService.playClick();
        setSelectedFixture(fixture);
    }

    const handleTeamClick = (e: React.MouseEvent, teamName: string) => {
        e.stopPropagation();
        pushView({ type: 'teamDetail', title: `Team: ${teamName}`, teamName: teamName });
    };

    const getRoundNameForFixture = useCallback((fixture: DisplayFixture): string | undefined => {
        for (const cup of Object.values(gameState.cups)) {
            if (cup.name === fixture.competition) {
                const round = cup.rounds?.find(r => Math.floor(r.week) === Math.floor(fixture.week));
                return round?.name;
            }
        }
        return undefined;
    }, [gameState.cups]);

    return (
        <>
            <div className="panel">
                <div className="panel-header">All Fixtures</div>
                <div style={{ display: 'flex', gap: '8px', padding: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                     <label htmlFor="league-select" style={{marginBottom: 0, flexShrink: 0}}>League:</label>
                    <select id="league-select" value={selectedLeague} onChange={(e) => setSelectedLeague(e.target.value)} style={{ flex: '1 1 150px', minWidth: '120px' }}>
                        <option value="ALL">All Leagues & Cups</option>
                        {leagueNames.map(name => <option key={name} value={name}>{name}</option>)}
                    </select>
                    <div style={{display: 'flex', flex: '2 1 250px', gap: '8px'}}>
                        <button onClick={() => handleWeekChange(Math.max(1, week - 1))}>&lt;</button>
                        <select value={week} onChange={(e) => handleWeekChange(Number(e.target.value))} style={{ flex: '1' }}>
                            {Array.from({ length: weeksPerSeason }, (_, i) => i + 1).map(w => <option key={w} value={w}>Week {w}</option>)}
                        </select>
                        <button onClick={() => handleWeekChange(Math.min(weeksPerSeason, week + 1))}>&gt;</button>
                    </div>
                </div>
                <div className="table-wrapper">
                     <table className="table">
                        <thead>
                            <tr><th>Comp</th><th>Home</th><th>Away</th><th>Result</th></tr>
                        </thead>
                        <tbody>
                            {weekFixtures.length > 0 ? (
                                weekFixtures.map((f, i) => {
                                    const roundName = getRoundNameForFixture(f);
                                    const compDisplay = roundName ? `${f.competition} - ${roundName}` : f.competition;
                                    return (
                                     <tr key={`${f.week}-${f.home}-${f.away}-${f.competition}`}
                                         className={`${f.result ? 'clickable-fixture' : ''} ${f.home === gameState.playerTeamName || f.away === gameState.playerTeamName ? 'highlight' : ''}`}
                                         onClick={() => handleFixtureClick(f)}>
                                        <td title={compDisplay}>{getCompetitionEmoji(f.competition, gameState)} {compDisplay}</td>
                                        <td><button className="link-style" onClick={(e) => handleTeamClick(e, f.home)}><span className={f.home === gameState.playerTeamName ? 'player-team-name' : ''}>{getTeamByName(f.home)?.badge && <TeamBadge badge={getTeamByName(f.home)?.badge} color={getTeamByName(f.home)?.colors.primary} size="sm" />}{f.home}</span></button></td>
                                        <td><button className="link-style" onClick={(e) => handleTeamClick(e, f.away)}><span className={f.away === gameState.playerTeamName ? 'player-team-name' : ''}>{getTeamByName(f.away)?.badge && <TeamBadge badge={getTeamByName(f.away)?.badge} color={getTeamByName(f.away)?.colors.primary} size="sm" />}{f.away}</span></button></td>
                                        <td style={{textAlign: 'center'}}>{f.result ? `${f.result.homeScore} - ${f.result.awayScore}` : '-'}</td>
                                    </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={4} style={{textAlign: 'center', padding: '20px', opacity: 0.7}}>
                                        {cupWeekInfo ? 'awaiting fixtures' : 'No fixtures this week'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {selectedFixture && (
                <MatchDetailsModal
                    isOpen={!!selectedFixture}
                    onClose={() => setSelectedFixture(null)}
                    fixture={selectedFixture}
                    getPlayerById={getPlayerById}
                    gameState={gameState}
                    pushView={pushView}
                    isAiPunditEnabled={isAiPunditEnabled}
                    showRatingsOnMatchReport={showRatingsOnMatchReport}
                    onCacheAiMatchReport={onCacheAiMatchReport}
                />
            )}
        </>
    );
};